import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel, Stat } from "../components/Panel";
import { workspaceStatusLabel } from "../lib/adminActions";
import {
  emptyAdminDashboard,
  fetchAdminDashboard,
  type AdminDashboardData
} from "../lib/api/adminDashboardClient";
import { formatPackageName, getRemainingDays } from "../lib/packageLimits";

export function Admin() {
  const [dashboard, setDashboard] = useState<AdminDashboardData>(emptyAdminDashboard);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let isMounted = true;

    async function loadDashboard() {
      setIsLoading(true);
      setError(undefined);
      try {
        const nextDashboard = await fetchAdminDashboard();
        if (isMounted) setDashboard(nextDashboard);
      } catch (caught) {
        if (isMounted) {
          setError(caught instanceof Error ? caught.message : "Admin-Daten konnten nicht geladen werden.");
          setDashboard(emptyAdminDashboard);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void loadDashboard();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Layout
      eyebrow="HAUFFE Admin"
      title="HAUFFE Admin"
      description="Workspaces verwalten, Zugänge aktivieren und interne technische Nutzung überwachen."
    >
      <div className="toolbar">
        <a className="button button-primary" href="/admin/workspaces/new">
          Workspace anlegen
        </a>
      </div>

      <div className="grid grid-4">
        <Stat label="Organizations" value={String(dashboard.counts.organizations)} />
        <Stat label="Workspaces" value={String(dashboard.counts.workspaces)} />
        <Stat label="Users" value={String(dashboard.counts.users)} />
        <Stat label="Onboarding Requests" value={String(dashboard.counts.onboardingRequests)} />
        <Stat label="Aktiv" value={String(dashboard.counts.activeWorkspaces)} />
        <Stat label="Archiviert" value={String(dashboard.counts.archivedWorkspaces)} />
        <Stat label="Abgelaufen" value={String(dashboard.counts.expiredWorkspaces)} />
      </div>

      <Panel title="Workspace-Liste">
        {isLoading ? <p>Workspaces werden geladen...</p> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        {!isLoading && !error && dashboard.workspaces.length === 0 ? (
          <div className="empty-state">
            <p><strong>Noch keine Workspaces vorhanden.</strong></p>
            <p>Als nächstes Admin-Provisioning oder Self-Onboarding einrichten.</p>
          </div>
        ) : null}
        <div className="table-like admin-table">
          {dashboard.workspaces.map((workspace) => {
            const detailUrl = `/admin/workspaces/${workspace.id}`;
            return (
              <div key={workspace.id}>
                <strong>
                  <a href={detailUrl}>{workspace.name}</a>
                </strong>
                <span>{workspace.organizationName ?? "Keine Organization"}</span>
                <span>{formatPackageName(workspace.packageType)}</span>
                <span className={`status-badge status-${workspace.status}`}>{workspaceStatusLabel(workspace.status)}</span>
                <span>{workspace.startDate} – {workspace.endDate}</span>
                <span>{getRemainingDays(workspace.endDate)} Tage</span>
                <span>{workspace.departmentCount}/{workspace.maxDepartments} Abteilungen</span>
                <span>{workspace.submittedDepartmentCount} eingereicht</span>
                <span>{workspace.aiCallsUsed} AI Calls</span>
                <span>{workspace.techCostEstimateEur.toFixed(2)} EUR</span>
                <span>{workspace.usageStatus}</span>
                <a className="button button-secondary" href={detailUrl}>
                  Workspace öffnen
                </a>
                <a className="button button-primary" href={detailUrl}>
                  Verwalten
                </a>
              </div>
            );
          })}
        </div>
      </Panel>
    </Layout>
  );
}
